import { useSearchParams, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ProductImages from "./components/ProductImages";
import ProductInfo from "./components/ProductInfo";
import ProductReviews from "./components/ProductReviews";
import { mockInfluencers } from "@/mocks/influencers";
import { Link } from "react-router-dom";
import SEO from "@/components/base/SEO";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

export default function ProductDetailPage() {
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const refCode = searchParams.get("ref") || "";
  const referrer = mockInfluencers.find((inf) => inf.handle.replace("@", "") === refCode);

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Premium Wireless Earbuds Pro X200",
    "description": "High-quality wireless earbuds from a verified USA factory. Active noise cancellation, 30-hour battery life, and premium sound quality.",
    "url": `${SITE_URL}/product/${id || "1"}`,
    "sku": `SBP-PROD-${id || "1"}`,
    "image": `${SITE_URL}/og-image.jpg`,
    "brand": {
      "@type": "Brand",
      "name": "TechVision Factory",
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "USD",
      "price": "49.99",
      "availability": "https://schema.org/InStock",
      "url": `${SITE_URL}/product/${id || "1"}`,
      "seller": {
        "@type": "Organization",
        "name": "TechVision Factory",
      },
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "2847",
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Premium Wireless Earbuds Pro X200 — TechVision Factory | StarBookPrime"
        description="Buy Premium Wireless Earbuds Pro X200 direct from TechVision Factory. Active noise cancellation, 30-hour battery, verified USA manufacturer. Earn 10% reseller commission."
        keywords="wireless earbuds factory direct, premium earbuds wholesale, verified USA electronics"
        canonical={`/product/${id || "1"}`}
        schema={productSchema}
      />
      <Navbar />
      <div className="pt-16">
        {/* Referral Banner */}
        {referrer && (
          <div className="bg-gradient-to-r from-rose-500 to-orange-500 text-white py-2.5 px-4 text-center text-sm font-medium">
            <i className="ri-star-fill mr-2" />
            You were referred by <strong>{referrer.name}</strong> ({referrer.handle}) — Special influencer pricing applied!
          </div>
        )}

        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-gray-400 mb-6">
            <Link to="/" className="hover:text-orange-500 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/marketplace" className="hover:text-orange-500 transition-colors cursor-pointer">Marketplace</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-gray-600">Premium Wireless Earbuds Pro X200</span>
          </nav>

          {/* Main Product Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-12">
            <ProductImages />
            <ProductInfo referralCode={refCode} />
          </div>

          {/* Influencer Referrals Section */}
          <div className="mb-12 bg-gradient-to-r from-rose-50 to-orange-50 border border-rose-100 rounded-2xl p-6">
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-star-fill text-rose-500" /> Top Influencers Promoting This Product
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {mockInfluencers.slice(0, 3).map((inf) => {
                const platformIcons: Record<string, string> = { tiktok: "ri-tiktok-line", youtube: "ri-youtube-line", twitter: "ri-twitter-x-line" };
                const refLink = `${window.location.origin}/product/1?ref=${inf.handle.replace("@", "")}`;
                return (
                  <div key={inf.id} className="bg-white rounded-xl p-4 border border-rose-100">
                    <div className="flex items-center gap-3 mb-3">
                      <img src={inf.avatar} alt={inf.name} className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{inf.name}</p>
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <i className={platformIcons[inf.platform] || "ri-star-line"} />
                          <span>{inf.followers} followers</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-gray-500">{inf.sales} sales</span>
                      <span className="text-xs font-bold text-green-600">{inf.commission} commission</span>
                    </div>
                    <a
                      href={refLink}
                      className="block w-full text-center py-2 bg-rose-500 text-white text-xs font-semibold rounded-lg hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Shop via {inf.name.split(" ")[0]}&apos;s Link
                    </a>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Reviews */}
          <ProductReviews />
        </div>
      </div>
      <Footer />
    </div>
  );
}
